import { Calendar as CalendarIcon } from '@mos-cat/ds-icons';
import { SystemStyleObject } from '@styled-system/css';

import invariant from 'tiny-invariant';
import {
  Popover,
  PopoverDisclosure,
  usePopoverState,
} from '../../Popover/Popover';

import { Box } from '../../Box/Box';
import { kind } from '../../Button/variants';
import {
  Calendar,
  CalendarProps,
  generateCalendarId,
  getCalendarDisclosureLabel,
} from '../../Calendar/Calendar';
import { UtilityButton } from '../../UtilityButton/UtilityButton';
import pxToRem from '../../utils/pxToRem';
import type { CommonDatepickerProps, DatepickerCalendarProps } from '../types';

export type ButtonKindProps = 'secondary' | 'tertiary';

type Props = Pick<CalendarProps, 'selectedDate' | 'onSelectedDateChange'> &
  DatepickerCalendarProps &
  Pick<CommonDatepickerProps, 'disabled'> & { buttonKind?: ButtonKindProps };

function CalendarPopover({
  disabled,
  selectedDate,
  buttonKind = 'secondary',
  onSelectedDateChange,
  ...calendarProps
}: Props) {
  invariant(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Отключаем проверку на избыточность условия, так как нам нужна проверка в runtime.
    buttonKind === 'secondary' || buttonKind === 'tertiary',
    'Тип кнопки должен быть secondary или tertiary'
  );

  const popoverState = usePopoverState();
  const popoverCalendarId = generateCalendarId(popoverState.baseId);

  const handleDateChange = (date: string | undefined) => {
    onSelectedDateChange(date);
    popoverState.hide();
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- в либе styled-system variants возвращает any
  let buttonStyles = kind({ kind: buttonKind });
  if (buttonKind === 'tertiary') {
    Object.assign(buttonStyles, {
      '&&': {
        borderColor: 'datepickerButtonTertiaryEnable',
      },
      '&&:hover': {
        borderColor: 'datepickerButtonTertiaryHover',
      },
      '&&:active': {
        borderColor: 'datepickerButtonTertiaryActive',
      },
      '&&[aria-disabled="true"]': {
        borderColor: 'datepickerButtonTertiaryDisable',
      },
    });
  }

  return (
    <Box>
      <PopoverDisclosure
        as={UtilityButton}
        {...popoverState}
        aria-label={getCalendarDisclosureLabel({ selectedDate })}
        disabled={disabled}
        focusable
        styles={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 'sp-4',
          height: pxToRem(48),
          width: pxToRem(48),
          borderRadius: 'button',
          border: '1',
          ...(buttonStyles as SystemStyleObject),
        }}
      >
        <Box as={CalendarIcon} size="datepickerBtnIconSize" />
      </PopoverDisclosure>
      <Box
        as={Popover}
        {...popoverState}
        modal
        preventBodyScroll={false}
        aria-labelledby={popoverCalendarId}
        styles={{
          zIndex: 100,
          fontFamily: 'main',
        }}
      >
        <Calendar
          {...popoverState}
          {...calendarProps}
          autofocus
          calendarId={popoverCalendarId}
          selectedDate={selectedDate}
          onSelectedDateChange={handleDateChange}
        />
      </Box>
    </Box>
  );
}

export default CalendarPopover;
